/**
 * Cross-browser, mobile-safe clipboard helper.
 *
 * `navigator.clipboard.writeText` silently fails in several common cases that hit us
 * on mobile: insecure (http) contexts, older in-app browsers (LinkedIn / FB / WeChat
 * webviews), and some iOS PWA contexts. We detect those and fall back to the legacy
 * `document.execCommand('copy')` path with a hidden textarea, which works nearly
 * everywhere.
 *
 * Returns `true` on success, `false` on failure — callers can use this to toast the
 * right message instead of assuming success.
 */
const copyToClipboard = async (text) => {
  const value = String(text ?? '');
  if (!value) return false;

  // Prefer the modern API when it's actually available and permitted.
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function' &&
    // Secure context is required for clipboard API on most browsers.
    (typeof window === 'undefined' || window.isSecureContext)
  ) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Fall through to the textarea fallback.
    }
  }

  // Legacy fallback: works on http, inside webviews, on iOS Safari.
  if (typeof document === 'undefined') return false;
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.opacity = '0';
    ta.style.pointerEvents = 'none';
    document.body.appendChild(ta);
    // iOS needs the element in the DOM, non-readonly momentarily, and a real selection.
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, value.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

export default copyToClipboard;
